import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { Panel } from "@src/features/experienceBuilder/modifications/common/panel/Panel";
import { ModificationHeader } from "@src/features/experienceBuilder/modifications/common/ModificationHeader";
import { AgenticLogicSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/AgenticLogicSection";
import { DiscountSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/DiscountSection";
import { GeneralSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/GeneralSection";
import { RulesSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/RulesSection";
import { UpsellPlacementSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/UpsellPlacementSection";
import { TemplateSection } from "@src/features/experienceBuilder/modifications/components/onsiteUpsell/TemplateSection";
import { useDispatch, useSelector } from "@src/store";
import {
  ExperienceBuilderAction as Action,
  ExperienceBuilderSelector as Selector,
} from "@src/store/slices/experienceBuilder";
import type { OnsiteUpsellComponent } from "@src/store/slices/experienceBuilder/types";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import { assert } from "@src/utils/assert";

export const OnsiteUpsellComponentPanel = () => {
  const dispatch = useDispatch();
  // Mirrors the backend gate exactly (ConfigProvider.isAgenticLogicEnabled).
  // An org that is not on the engine has no rules to bind to.
  const agenticLogicEnabled = useIsAgenticLogicEnabled();

  const mod = useSelector(Selector.component.getVariationDraft) as
    | OnsiteUpsellComponent
    | undefined;
  assert(mod !== undefined, "mod is undefined");
  const isApplicable = useSelector(Selector.isVariationDraftApplicable);
  const errors = useSelector(Selector.getVariationDraftUIErrors);

  // Engine orgs must pick a rule — the legacy editor is never offered to them.
  // The gate lives here rather than in the mod handler because the flag comes
  // from react-query and `getErrors` is pure.
  const ruleMissing = agenticLogicEnabled && mod.agenticLogicRuleId == null;

  const update = (changes: Partial<OnsiteUpsellComponent>) =>
    dispatch(Action.component.setDraft({ ...mod, ...changes }));
  const cancel = () => void dispatch(Action.cancelDraft({ trackCancel: true }));
  const apply = () => void dispatch(Action.applyDraft());

  return (
    <>
      <ModificationHeader />
      <Panel>
        <Panel.Body>
          <UpsellPlacementSection mod={mod} errors={errors} onChange={update} />

          <IgDivider />

          {!agenticLogicEnabled && (
            <>
              <GeneralSection
                location={mod.location}
                showExclusions
                maxProducts={mod.maxProducts}
                excludeOutOfStock={mod.excludeOutOfStock}
                excludeItemsInCart={mod.excludeItemsInCart}
                excludePdpProduct={mod.excludePdpProduct}
                onChange={update}
                showMaxProducts
              />

              <IgDivider />
            </>
          )}

          <DiscountSection
            discount={mod.discount}
            valueError={Boolean(errors?.["discount.discountValue"])}
            onChange={(discount) => update({ discount })}
          />

          <IgDivider />

          <Panel.Header>Rules</Panel.Header>
          {/* One editor per org, split on the engine flag: engine orgs select
              an Agentic Logic rule (required — the Apply gate below), legacy
              orgs keep the legacy rules editor. Never both. */}
          {agenticLogicEnabled ? (
            <AgenticLogicSection mod={mod} onChange={update} />
          ) : (
            <RulesSection
              rules={mod.rules}
              location={mod.location}
              onChange={(rules) => update({ rules })}
            />
          )}

          <IgDivider />

          <TemplateSection template={mod.template} onChange={update} />
        </Panel.Body>

        <Panel.Buttons>
          <Panel.CancelButton onClick={() => void cancel()} />
          <Panel.ApplyButton
            onClick={apply}
            disabled={!isApplicable || ruleMissing}
          />
        </Panel.Buttons>
      </Panel>
    </>
  );
};
